
import React from "react";
import { ClassificationNode, AssetType } from "@/types/classifications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { logger } from "@/services/LoggingService";

interface ClassificationEditorProps {
  node: ClassificationNode;
  onSave: (node: ClassificationNode) => void;
  onCancel: () => void;
}

const ASSET_TYPES: AssetType[] = [
  "equity",
  "fixed_income",
  "cash",
  "alternative", // Adding the alternative type
  "hedge_fund",
  "private_equity",
  "private_credit",
  "private_debt",
  "real_estate",
  "digital_asset",
  "collectible",
  "commodity"
];

const formatAssetType = (type: string) => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export function ClassificationEditor({ node, onSave, onCancel }: ClassificationEditorProps) {
  const [name, setName] = React.useState(node.name);
  const [selectedType, setSelectedType] = React.useState<string>(node.type || "unset");

  React.useEffect(() => {
    logger.debug('classification.editor', 'Editor initialized', {
      initialType: node.type,
      initialName: node.name,
      nodeId: node.id,
      availableTypes: ASSET_TYPES
    });
  }, [node]);

  const handleTypeChange = (value: string) => {
    logger.debug('classification.editor', 'Type selection change', {
      previousType: selectedType,
      newType: value,
      nodeId: node.id
    });
    setSelectedType(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    
    const updatedNode = {
      ...node,
      name,
      type: (selectedType === "unset" ? undefined : selectedType) as AssetType | undefined
    };

    logger.debug('classification.editor', 'Saving node', {
      updatedNode,
      previousType: node.type,
      newType: selectedType,
      nodeId: node.id
    });

    onSave(updatedNode);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pt-6">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter classification name"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Asset Type</Label>
        <Select
          value={selectedType}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger id="type" className="w-full">
            <SelectValue placeholder="Select an asset type" />
          </SelectTrigger>
          <SelectContent 
            className="w-[--radix-select-trigger-width]" 
            position="popper" 
            sideOffset={4}
            style={{ zIndex: 1000 }}
          >
            <SelectItem value="unset">Select an asset type</SelectItem>
            {ASSET_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {formatAssetType(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!name}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}
